'use client';

import { useState } from 'react';

import Link from 'next/link';

import { Plus } from 'lucide-react';

/**
 * "CREATE YOUR OWN" tile injected into the character grids (home + catalog).
 * The trio artwork fills the card; the title, subtitle and the glowing
 * CREATE NOW button sit in its dark lower part. Sizes use container units so
 * the tile scales with the grid column. Links to /create.
 */
export function CreateCharacterPromoCard() {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      href="/create"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label="Create your own AI character"
      className="relative block w-full overflow-hidden rounded-3xl text-center"
      style={{
        containerType: 'inline-size',
        aspectRatio: '324 / 520',
        background:
          "url('/images/backgrounds/create-own-card.webp') center bottom / cover no-repeat, #1a0b24",
        border: '1px solid rgba(244,114,182,0.45)',
        transition:
          'transform 0.4s ease, filter 0.4s ease, box-shadow 0.4s ease',
        transform: hovered ? 'scale(1.02)' : 'scale(1)',
        filter: hovered ? 'brightness(1.08)' : 'brightness(1)',
        boxShadow: hovered
          ? '0 0 34px rgba(236,72,153,0.6), 0 0 85px rgba(190,24,93,0.5)'
          : '0 0 10px rgba(236,72,153,0.2)',
      }}
    >
      {/* Bottom scrim so the text reads over the bed */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0"
        style={{
          height: '48%',
          background:
            'linear-gradient(to top, rgba(26,8,36,0.95) 0%, rgba(26,8,36,0.75) 50%, transparent 100%)',
        }}
      />

      <span
        className="absolute inset-x-0 bottom-0 flex flex-col items-center"
        style={{ padding: '0 8cqw 9cqw', gap: '4cqw' }}
      >
        <span
          className="flex flex-col items-center italic"
          style={{
            fontWeight: 900,
            lineHeight: 0.98,
            letterSpacing: '-0.01em',
            transition: 'transform 0.4s ease',
            transform: hovered ? 'scale(1.04)' : 'scale(1)',
          }}
        >
          <span className="text-white" style={{ fontSize: '14.5cqw' }}>
            CREATE
          </span>
          <span
            style={{
              fontSize: '14.5cqw',
              background:
                'linear-gradient(180deg,#ffb3dd 0%,#f472b6 45%,#ec4899 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              filter: 'drop-shadow(0 2px 14px rgba(236,72,153,0.5))',
            }}
          >
            YOUR OWN
          </span>
        </span>

        <span
          className="text-white"
          style={{ fontSize: '5cqw', lineHeight: 1.35, maxWidth: '84cqw' }}
        >
          Build your dream companion and make every detail yours.
        </span>

        <span
          className="relative inline-flex w-full items-center justify-center overflow-hidden text-white"
          style={{
            marginTop: '2cqw',
            padding: '4.2cqw 4cqw',
            fontSize: '6.2cqw',
            fontWeight: 800,
            letterSpacing: '0.02em',
            borderRadius: '999px',
            background: 'linear-gradient(180deg,#ff3fb4 0%,#ec1c8f 100%)',
            border: '1px solid rgba(255,182,224,0.7)',
            boxShadow:
              '0 0 26px rgba(255,46,170,0.75), 0 0 60px rgba(236,72,153,0.45)',
            transition: 'transform 0.4s ease',
            transform: hovered ? 'scale(1.04)' : 'scale(1)',
          }}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.5) 50%, transparent 65%)',
              transition: 'transform 0.7s ease-out',
              transform: hovered ? 'translateX(100%)' : 'translateX(-100%)',
            }}
          />
          <span
            className="relative z-10 inline-flex items-center"
            style={{ gap: '3cqw' }}
          >
            CREATE NOW
            <Plus
              style={{ width: '6.4cqw', height: '6.4cqw' }}
              strokeWidth={2.4}
            />
          </span>
        </span>
      </span>
    </Link>
  );
}
